'use client';

import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { tv } from 'tailwind-variants';
import type videojs from 'video.js';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { Image } from '@/app_components/common/Image';
import { cn } from '@/app_lib/utils/utils';
import { placeholderFortuneImage } from '@/appconstants';

import 'video.js/dist/video-js.css';

type VideoJsPlayer = ReturnType<typeof videojs>;

function guessSourceType(url: string): string {
  const path = url.split('?')[0]?.toLowerCase() ?? '';
  if (path.endsWith('.m3u8')) {
    return 'application/x-mpegURL';
  }
  if (path.endsWith('.mp4')) {
    return 'video/mp4';
  }
  if (path.endsWith('.webm')) {
    return 'video/webm';
  }
  return 'application/x-mpegURL';
}

const videoStyles = tv({
  slots: {
    close: '',
    container: '',
    iframe: '',
  },
  variants: {
    dockingMode: {
      false: {
        close: 'hidden',
        container: 'w-full h-full aspect-video',
        iframe: 'w-full h-full aspect-video',
      },
      true: {
        close: 'absolute top-0 right-0 w-5 z-[10001] text-primary-inverse',
        container: 'w-[300px] h-[169px] fixed bottom-[70px] right-4 z-header-popover',
        iframe: 'w-[300px] h-[169px] fixed bottom-[70px] right-4 z-header-popover',
      },
    },
  },
});

export interface CustomVideoProps {
  videoUrl?: string | null;
  className?: string;
  dataCy?: string;
  videoPosterCy?: string;
  autoplay?: boolean;
  docking?: boolean;
  loading?: React.ReactNode;
  onEnd?: () => void;
}

/** @deprecated Don't use. This is temporary for Conferences Videos until we replatform video site */
export const CustomVideo = ({
  videoUrl,
  className = '',
  dataCy = '',
  videoPosterCy = '',
  autoplay = true,
  docking = false,
  loading = null,
  onEnd,
}: CustomVideoProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  const [play, setPlay] = useState(false);
  const [dockingMode, setDockingMode] = useState(false);
  const [disableDockingMode, setDisableDockingMode] = useState(false);
  const [videoContainerRef, inViewPort] = useInView({ threshold: 0.5 });

  const loadingContent = loading ?? (
    <Image
      alt='Video Poster'
      className='w-full h-full aspect-video'
      data-cy={videoPosterCy}
      height={450}
      priority
      src={`${placeholderFortuneImage}?resize=800,450`}
      width={800}
    />
  );

  useEffect(() => {
    if (!(play || disableDockingMode)) {
      const timer = setTimeout(() => {
        if (inViewPort) {
          setPlay(true);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (play && !inViewPort && docking) {
      setDockingMode(true);
    }

    if (play && inViewPort && docking) {
      setDockingMode(false);
    }
    return () => {};
  }, [disableDockingMode, inViewPort, play, docking]);

  useEffect(() => {
    if (!play) {
      return;
    }
    if (!videoUrl) {
      return;
    }
    if (!videoRef.current) {
      return;
    }

    let cancelled = false;

    const init = async () => {
      const videoJs = (await import('video.js')).default;
      if (cancelled) {
        return;
      }
      const mountEl = videoRef.current;
      if (!mountEl) {
        return;
      }

      playerRef.current?.dispose();
      playerRef.current = null;

      const player = videoJs(mountEl, {
        autoplay: autoplay ? 'any' : false,
        controls: true,
        fluid: true,
        loop: false,
        responsive: true,
        sources: [{ src: videoUrl, type: guessSourceType(videoUrl) }],
      });
      player.on('ended', () => {
        onEndRef.current?.();
      });
      playerRef.current = player;
    };

    void init();

    return () => {
      cancelled = true;
      playerRef.current?.dispose();
      playerRef.current = null;
    };
  }, [play, videoUrl, autoplay]);

  const showPlayer = play && Boolean(videoUrl);

  return (
    <div
      className={cn('relative block w-full aspect-video', className)}
      ref={videoContainerRef}
    >
      <div
        className={videoStyles().container({ dockingMode })}
        data-cy={dataCy}
      >
        <ActionableIcon
          className={videoStyles().close({ dockingMode })}
          onClick={() => {
            if (docking) {
              setDockingMode(false);
              setDisableDockingMode(true);
            }
            setPlay(false);
          }}
        >
          <Icon icon='X' />
        </ActionableIcon>
        {showPlayer ? (
          <div className={cn('w-full h-full', videoStyles().iframe({ dockingMode }))}>
            {/* biome-ignore lint/a11y/useMediaCaption: captions come from stream / VOD; not wired in this temp player */}
            <video
              className='video-js vjs-big-play-centered w-full h-full'
              key={videoUrl}
              playsInline
              ref={videoRef}
            />
          </div>
        ) : (
          <div className='w-full h-full flex items-center justify-center'>{loadingContent}</div>
        )}
      </div>
    </div>
  );
};
